import { z } from 'zod';
import {
  VERSIONED_DEFAULT_SORT,
  renameRequestSchemaFor,
  saveDraftRequestSchema,
  saveVersionRequestSchema,
  versionedCardShape,
  versionedContentSchema,
  versionedDetailSchema,
  versionedFiltersShape,
  versionedSortSchema,
} from './versioning.js';

/**
 * A persona: the VOICE the interviewer speaks with (refonte modulaire §10).
 *
 * Second consumer of the common versioning pattern (§4), and the one that
 * measures whether the abstraction holds (§19). Everything generic - the
 * versions, the two pointers, the draft, the retirement, « utilisé dans » -
 * comes from `versioning.ts`; what is here is only what depends on what a
 * persona MEANS.
 *
 * ## Two names, and they do not serve the same thing
 *
 * `name` is the OPERATOR's name: the cards, the selectors, the history, and it
 * is unique among the non-retired personas (§4). The person interviewed never
 * sees it. `firstName` is the name the PERSON sees, and it exists only in a
 * named presentation. Merging the two would either forbid two personas called
 * Claire, or show « Claire empathique v2 » inside a chat bubble.
 *
 * ## What a version COPIES, and why
 *
 * The involvement level and the tone are rows of two reference lists that are
 * NOT versioned (§11.5). A version copies what it needs from them, and what is
 * served to the model is always that frozen copy - never the current scale or
 * the current library. Rewriting a level therefore changes no version already
 * saved; deleting a tone changes no persona at all.
 */

/* --------------------------------------------------------------- the bounds */

/**
 * H13 (§16). Bounds of INPUT, not of meaning: an operator meets them by
 * mistake, never on purpose. They are the numbers the mockup counts down from.
 */
export const PERSONA_NAME_MAX = 80;
export const PERSONA_DIRECTION_MAX = 4000;
export const PERSONA_FIRST_NAME_MAX = 40;
export const PERSONA_PRESENTATION_DIRECTION_MAX = 600;
export const PERSONA_BACKGROUND_MAX = 4000;
export const INVOLVEMENT_LEVEL_NAME_MAX = 60;
export const INVOLVEMENT_LEVEL_DIRECTION_MAX = 2000;
export const PERSONA_TONE_NAME_MAX = 60;
export const PERSONA_TONE_TEXT_MAX = 2000;

/**
 * Every text field is TRIMMED before it is measured, and that order is the
 * decision: a field holding three spaces is a direction an operator believes
 * they wrote, and it would reach the model as a blank line.
 */
const firstNameField = z.string().trim().min(1).max(PERSONA_FIRST_NAME_MAX);
const presentationDirectionField = z.string().trim().min(1).max(PERSONA_PRESENTATION_DIRECTION_MAX);

/* --------------------------------------------------------- the presentation */

/** The two exclusive forms of §10.1. `kind` is the discriminant of both unions below. */
export const personaPresentationKindSchema = z.enum(['anonymous', 'named']);
export type PersonaPresentationKind = z.infer<typeof personaPresentationKindSchema>;

/**
 * How the persona introduces itself, in a SAVED version.
 *
 * A discriminated union rather than three nullable fields, because the two
 * shapes have different obligations: named REQUIRES both the first name and the
 * self-introduction direction, anonymous forbids the question. Served as a
 * consigne and never pasted as-is - « dis en une phrase que tu travailles sur
 * les récits de métier », not « Bonjour, je m'appelle Claire » (§10.1).
 *
 * NOT `.strict()`, like every stored shape (§4.1): a field added to the named
 * form later must not make today's rows unreadable.
 */
export const personaPresentationSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('anonymous') }),
  z.object({
    kind: z.literal('named'),
    firstName: firstNameField,
    direction: presentationDirectionField,
  }),
]);
export type PersonaPresentation = z.infer<typeof personaPresentationSchema>;

/**
 * The same, in a DRAFT - and it is deliberately NOT a discriminated union.
 *
 * §10.1: « repasser en anonyme les cache sans les effacer du brouillon, pour
 * qu'un aller-retour ne coûte pas une ressaisie ». Under the union above, an
 * anonymous draft has nowhere to keep the first name, so a reload - the very
 * thing the server draft exists to survive - would eat what was typed. A flat
 * object with two optional fields keeps them beside a `kind` that hides them.
 *
 * The bounds still apply to what IS there: a 60-character first name is refused
 * as a draft too, because it could never become a version.
 */
export const personaDraftPresentationSchema = z.object({
  kind: personaPresentationKindSchema,
  firstName: firstNameField.optional(),
  direction: presentationDirectionField.optional(),
});
export type PersonaDraftPresentation = z.infer<typeof personaDraftPresentationSchema>;

/* ---------------------------------------------------------- the involvement */

/**
 * The involvement, COPIED into the version from the scale of §11.5.
 *
 * `overridden` is the decision of the mockup review of 2026-09-09, and it flips
 * the default: FALSE means the direction copied here is the level's own wording
 * AT THE MOMENT OF THE SAVE, so a persona nobody redefined follows the last
 * rewriting of its level at each new version. TRUE means an operator redefined
 * it for this persona, and rewriting the level no longer touches it.
 *
 * It is `optional` with a default of `false` at read time, and that is §4.1
 * rather than politeness: rows written before this field existed must keep
 * parsing, and a `.strict()` object with a required `overridden` would break
 * every one of them silently.
 *
 * `levelId` only positions the slider. A level RETIRED from the scale leaves a
 * note in the editor and invalidates NOTHING - not the persona, not its
 * referents, not the sessions (§10.1).
 */
export const personaInvolvementSchema = z.object({
  levelId: z.string().min(1),
  name: z.string().trim().min(1).max(INVOLVEMENT_LEVEL_NAME_MAX),
  direction: z.string().trim().min(1).max(INVOLVEMENT_LEVEL_DIRECTION_MAX),
  overridden: z.boolean().default(false),
});
export type PersonaInvolvement = z.infer<typeof personaInvolvementSchema>;

/* ----------------------------------------------------------------- the tone */

/**
 * The tone, COPIED from the library of §11.5 - both fields together or nothing.
 *
 * No provenance is kept, on purpose: a persona does not know which library
 * entry it came from, so renaming, rewriting or deleting a tone changes no
 * persona, published or not (§10.1). The editor shows a tone that has left the
 * library as a tag apart, from this copy alone.
 */
export const personaToneSchema = z.object({
  name: z.string().trim().min(1).max(PERSONA_TONE_NAME_MAX),
  text: z.string().trim().min(1).max(PERSONA_TONE_TEXT_MAX),
});
export type PersonaTone = z.infer<typeof personaToneSchema>;

/* -------------------------------------------------------------- the content */

/**
 * The CONTENT of one version - the totality of the persona at that version,
 * name included (§4.1).
 *
 * **Deliberately not `.strict()`** (§4.1): this schema parses rows written by
 * earlier versions of the code. A new field arrives `optional` with a default
 * at read time; an unknown one is dropped rather than crashing a screen on a
 * base nobody can migrate backwards.
 *
 * `background` - « Son histoire » on screen, `background` in code - is the one
 * text field that may legitimately be EMPTY, so it bottoms out at 0 and
 * defaults to the empty string rather than being nullable: a persona with no
 * story and a persona whose story was cleared are the same thing, and the
 * renderer omits the block either way.
 *
 * There is NOTHING here about « ne pas être une IA ». That rule left the
 * persona (§10.1): it holds for every persona and every session, so it is a
 * global rule seeded in Réglages (§11.3), and the editor carries only a
 * read-only note about it.
 */
export const personaContentSchema = z.object({
  name: z.string().trim().min(1).max(PERSONA_NAME_MAX),
  direction: z.string().trim().min(1).max(PERSONA_DIRECTION_MAX),
  presentation: personaPresentationSchema,
  involvement: personaInvolvementSchema,
  tone: personaToneSchema.nullable().default(null),
  background: z.string().trim().max(PERSONA_BACKGROUND_MAX).default(''),
});
export type PersonaContent = z.infer<typeof personaContentSchema>;

/**
 * The content of a DRAFT: the same fields, each optional, and the presentation
 * widened to the flat shape that can hold hidden fields.
 *
 * `min(1)` means an empty field is spelled by the key being ABSENT rather than
 * by an empty string, which is what lets a half-written persona survive a
 * reload. It is what the editor SENDS and what it READS BACK: one schema for
 * both halves of the round trip.
 */
export const personaDraftContentSchema = personaContentSchema.partial().extend({
  presentation: personaDraftPresentationSchema.optional(),
});
export type PersonaDraftContent = z.infer<typeof personaDraftContentSchema>;

/* ---------------------------------------------------------------- the cards */

/**
 * What the AVATAR and the SUBTITLE of a card are drawn from (mockup `Carte`):
 * the initial and « Claire · ton chaleureux », or the anonymous pictogram and
 * « Anonyme · sans ton ».
 *
 * The card carries the first name and NOT the self-introduction direction: a
 * card is scanned, and the direction is a paragraph.
 */
export const personaCardPresentationSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('anonymous') }).strict(),
  z.object({ kind: z.literal('named'), firstName: z.string().min(1) }).strict(),
]);
export type PersonaCardPresentation = z.infer<typeof personaCardPresentationSchema>;

/**
 * What the MINI-GAUGE of a card is drawn from.
 *
 * `levelRetired` is computed server side against the CURRENT scale, because
 * only the server holds both halves of the comparison in one read - the copy
 * frozen in the version, and the scale of §11.5. A retired level leaves the
 * gauge WITHOUT a position and carries no badge and no alert colour (mockup
 * review): a card is read at a glance, and it is the « Cran retiré » filter of
 * the list that finds these personas again.
 *
 * The card does not carry the level's POSITION: the page holds the scale
 * anyway - it needs it for the filter - and looking the id up there is what
 * keeps a stale position from being drawn beside a fresh scale.
 */
export const personaCardLevelSchema = z
  .object({
    levelId: z.string().min(1),
    name: z.string().min(1),
    levelRetired: z.boolean(),
  })
  .strict();
export type PersonaCardLevel = z.infer<typeof personaCardLevelSchema>;

/**
 * One card of the list (§4, §10): the thirteen common fields of
 * `versionedCardShape`, plus the three facets that belong to a persona.
 *
 * All three are read off the DEVELOPMENT version, like the criterion's example
 * count: a card shows what the persona IS, and a draft is what somebody is in
 * the middle of typing - `hasPendingDraft` is where that shows up.
 */
export const personaCardSchema = z
  .object({
    ...versionedCardShape,
    presentation: personaCardPresentationSchema,
    level: personaCardLevelSchema,
    /** The tone's name, or null when the persona carries none: « sans ton ». */
    toneName: z.string().min(1).nullable(),
  })
  .strict();
export type PersonaCard = z.infer<typeof personaCardSchema>;

/** The list, served WHOLE - retired entries included - and filtered in the page (§6, §4). */
export const personaListSchema = z.object({ personas: z.array(personaCardSchema) }).strict();
export type PersonaList = z.infer<typeof personaListSchema>;

/** Everything the editor reads in one call. */
export const personaDetailSchema = versionedDetailSchema(
  personaContentSchema,
  personaDraftContentSchema,
);
export type PersonaDetail = z.infer<typeof personaDetailSchema>;

/** One version read in full, for « Voir ». */
export const personaVersionSchema = versionedContentSchema(personaContentSchema);
export type PersonaVersion = z.infer<typeof personaVersionSchema>;

/** « Enregistrer une nouvelle version », with its optional note and its production checkbox. */
export const personaSaveVersionSchema = saveVersionRequestSchema(personaContentSchema);
export type PersonaSaveVersion = z.infer<typeof personaSaveVersionSchema>;

/** An autosave of the draft, on the partial content of `personaDraftContentSchema`. */
export const personaSaveDraftSchema = saveDraftRequestSchema(personaDraftContentSchema);
export type PersonaSaveDraft = z.infer<typeof personaSaveDraftSchema>;

/**
 * Renaming, on the persona's OWN name field - and it is the reason
 * `renameRequestSchemaFor` exists.
 *
 * H13 stops a persona's name at 80 where the layer's default stops at 120, and
 * a rename writes its name into the server draft that `personaDraftContent`
 * reads back. Under the default, a 90-character rename was accepted, committed
 * onto the entity AND the draft, and then refused by the draft schema on every
 * later read - of that persona and of the whole list, which builds a record per
 * entity. Built from the content's own field, the two bounds cannot diverge.
 */
export const personaRenameSchema = renameRequestSchemaFor(personaContentSchema.shape.name);
export type PersonaRename = z.infer<typeof personaRenameSchema>;

/* -------------------------------------------------------------- the filters */

/** The persona's first own filter (§10): how it introduces itself. */
export const personaPresentationFilterSchema = z.enum(['all', 'anonymous', 'named']);
export type PersonaPresentationFilter = z.infer<typeof personaPresentationFilterSchema>;

/**
 * The persona's second own filter: WHICH level it sits on.
 *
 * A bare string rather than an enum, because its options are rows of a table
 * that an operator edits - the scale of §11.5 - so no enum could be written
 * once and stay true. Two reserved values sit beside the identifiers: `all`,
 * the neutral state, and `retired`, which finds the personas whose level has
 * left the scale and which the card deliberately does not mark. A level
 * identifier is a uuid, so neither reserved word can ever collide with one.
 */
export const PERSONA_LEVEL_FILTER_ALL = 'all';
export const PERSONA_LEVEL_FILTER_RETIRED = 'retired';
export const personaLevelFilterSchema = z.string().min(1).default(PERSONA_LEVEL_FILTER_ALL);

/**
 * The four common sorts of §4, and no more.
 *
 * The persona adds NO sort of its own: neither of its two facets orders
 * anything an operator would look for - « les personas les plus impliqués »
 * is a filter, not a ranking - and a sort nobody asked for is a control
 * everybody has to read past.
 */
export const personaSortSchema = versionedSortSchema;
export type PersonaSort = z.infer<typeof personaSortSchema>;

/** The default of the list: by name (§4, issue #113). */
export const PERSONA_DEFAULT_SORT: PersonaSort = VERSIONED_DEFAULT_SORT;

/**
 * The thirteen common filters of §4 plus the persona's two, as ONE object.
 *
 * Every field is optional with a neutral default, so an absent parameter and a
 * reset filter are the same thing - which is what lets a filtered list be
 * pasted into a team thread as a short URL.
 */
export const personaFiltersSchema = z
  .object({
    ...versionedFiltersShape,
    presentation: personaPresentationFilterSchema.default('all'),
    level: personaLevelFilterSchema,
    sort: personaSortSchema.default(PERSONA_DEFAULT_SORT),
  })
  .strict();
export type PersonaFilters = z.infer<typeof personaFiltersSchema>;

/** The neutral state: every persona, retired excluded, sorted by name. */
export const PERSONA_FILTERS_DEFAULT: PersonaFilters = personaFiltersSchema.parse({});

/* ------------------------------------------- the two reference lists (§11.5) */

/**
 * One cran of the involvement scale (§11.5).
 *
 * NOT versioned, and that is why it is here rather than beside the persona's
 * content: a version copies what it needs from it, and the list itself is a
 * setting the team edits in place, with no history to walk back.
 *
 * `personaCount` is derived at read time - how many personas' DEVELOPMENT
 * version points at this level - and it exists for exactly one sentence: the
 * deletion confirmation of §11.5, which counts them. It is not a usage in the
 * sense of §4 (nothing is invalidated), so it does not go through
 * `versionedUsage`.
 */
export const involvementLevelSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1).max(INVOLVEMENT_LEVEL_NAME_MAX),
    direction: z.string().min(1).max(INVOLVEMENT_LEVEL_DIRECTION_MAX),
    /** Dense and zero-based, in the order the slider walks. */
    position: z.number().int().nonnegative(),
    personaCount: z.number().int().nonnegative(),
  })
  .strict();
export type InvolvementLevel = z.infer<typeof involvementLevelSchema>;

/** The whole scale, in order. Short by construction: it is a slider's worth of crans. */
export const involvementLevelListSchema = z
  .object({ levels: z.array(involvementLevelSchema) })
  .strict();
export type InvolvementLevelList = z.infer<typeof involvementLevelListSchema>;

/** The two fields a cran carries, whatever gesture writes them (§11.5). */
export const involvementLevelWriteSchema = z
  .object({
    name: z.string().trim().min(1).max(INVOLVEMENT_LEVEL_NAME_MAX),
    direction: z.string().trim().min(1).max(INVOLVEMENT_LEVEL_DIRECTION_MAX),
  })
  .strict();
export type InvolvementLevelWrite = z.infer<typeof involvementLevelWriteSchema>;

/**
 * The ceilings of a BLOCK save (issue #45), and they are bounds of
 * PLAUSIBILITY rather than of meaning - nobody will ever meet them.
 *
 * They exist because the shape they replace could not be abused: a write used
 * to carry ONE row, so one request meant one insert. A block carries a list, so
 * without a ceiling one authenticated request can ask the single SQLite
 * connection to apply an unbounded transaction - the uniqueness check building
 * two maps over the whole thing first. A slider of fifty crans and a cloud of
 * two hundred tags are already past anything §11.5 describes.
 */
export const INVOLVEMENT_LEVELS_BLOCK_MAX = 50;
export const PERSONA_TONES_BLOCK_MAX = 200;

/**
 * One row of a BLOCK save: the two fields, plus the id when the base has it.
 *
 * `id` absent means « create this one », and it is the only difference between
 * a rewrite and a creation in a block. Absent rather than null, because that is
 * what a row the operator has just typed carries: nothing.
 */
export const involvementLevelBlockRowSchema = involvementLevelWriteSchema.extend({
  id: z.string().min(1).optional(),
});
export type InvolvementLevelBlockRow = z.infer<typeof involvementLevelBlockRowSchema>;

/**
 * The whole scale, as the screen holds it, for ONE transaction (issue #45).
 *
 * The section saves « d'un bloc » (§11.5, planche `ReglagesPersonas`), and this
 * is that block: every row of the section, in the order the slider is to walk
 * them. THE ORDER OF THE ARRAY IS THE ORDER OF THE POSITIONS - there is no
 * `position` field to disagree with it.
 *
 * One request rather than a replay of per-row writes, and the two things that
 * buys are the reason issue #45 exists: swapping the names of two crans lands,
 * because uniqueness is judged on the state the block DESCRIBES rather than on
 * each write in turn; and a refusal leaves the base exactly as it was, where a
 * replay left it half applied.
 *
 * A row the block does not name is NOT deleted: deletion is its own gesture,
 * with its own confirmation and its count of personas (§11.5).
 */
export const involvementLevelBlockSchema = z
  .object({ levels: z.array(involvementLevelBlockRowSchema).max(INVOLVEMENT_LEVELS_BLOCK_MAX) })
  .strict();
export type InvolvementLevelBlock = z.infer<typeof involvementLevelBlockSchema>;

/** Two crans at minimum (§11.5): a slider with one position settles nothing. */
export const INVOLVEMENT_LEVELS_MIN = 2;

/**
 * One tone of the library (§11.5): a name and the text a version copies.
 *
 * No `personaCount` beside it, and the asymmetry with a cran is the decision of
 * §10.1: no provenance is kept at all, so the question « how many personas use
 * this tone » has no answer to give - and the deletion confirmation says
 * exactly that instead of a number.
 */
export const personaToneEntrySchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1).max(PERSONA_TONE_NAME_MAX),
    text: z.string().min(1).max(PERSONA_TONE_TEXT_MAX),
  })
  .strict();
export type PersonaToneEntry = z.infer<typeof personaToneEntrySchema>;

/** The whole library, sorted by NORMALIZED NAME - see `listTones` for why not by creation. */
export const personaToneListSchema = z.object({ tones: z.array(personaToneEntrySchema) }).strict();
export type PersonaToneList = z.infer<typeof personaToneListSchema>;

/** Creating a tone, or rewriting one. Same two fields either way. */
export const personaToneWriteSchema = z
  .object({
    name: z.string().trim().min(1).max(PERSONA_TONE_NAME_MAX),
    text: z.string().trim().min(1).max(PERSONA_TONE_TEXT_MAX),
  })
  .strict();
export type PersonaToneWrite = z.infer<typeof personaToneWriteSchema>;

/** One row of a block save. `id` absent means « create this one ». */
export const personaToneBlockRowSchema = personaToneWriteSchema.extend({
  id: z.string().min(1).optional(),
});
export type PersonaToneBlockRow = z.infer<typeof personaToneBlockRowSchema>;

/**
 * The whole library, as the screen holds it, for ONE transaction (issue #45).
 *
 * Same block as the scale beside it, minus the one thing a cloud of tags has
 * nothing to say about: the ORDER. The array carries no position, and the
 * answer comes back sorted by normalized name whatever order was sent.
 *
 * A tone the block does not name is left alone rather than deleted - deletion
 * is its own gesture (§11.5) - and it still counts for uniqueness: a tone the
 * « ajouter un ton » modal created while this screen was open holds its name.
 */
export const personaToneBlockSchema = z
  .object({ tones: z.array(personaToneBlockRowSchema).max(PERSONA_TONES_BLOCK_MAX) })
  .strict();
export type PersonaToneBlock = z.infer<typeof personaToneBlockSchema>;

/* ------------------------------------ the refusals of the two reference lists */

/**
 * What the two lists of §11.5 refuse, as one closed catalogue.
 *
 * The same doctrine as `versionedErrorCodeSchema`: a 409 with a machine code,
 * because a screen words a taken name (which NAMES the other row) differently
 * from a scale that would drop below two crans. They are not the refusals of
 * §4 and they do not share its enum - these lists are not versioned, so
 * `draft-stale` and `no-change` mean nothing here, and a shared enum would
 * offer six codes where two exist.
 */
export const personaReferenceErrorCodeSchema = z.enum(['name-taken', 'minimum-levels']);
export type PersonaReferenceErrorCode = z.infer<typeof personaReferenceErrorCodeSchema>;

/** `name` carries the OTHER row's name on `name-taken`, so the screen can point at it (H16). */
export const personaReferenceErrorSchema = z
  .object({
    error: z.string().min(1),
    code: personaReferenceErrorCodeSchema,
    name: z.string().optional(),
  })
  .strict();
export type PersonaReferenceError = z.infer<typeof personaReferenceErrorSchema>;
